import { useState, type FormEvent } from 'react'
import { Button } from '@floorfile/ui'
import { Copy, useCopy } from '@floorfile/i18n'
import { Link, useSearchParams } from 'react-router-dom'
import { apiFetch } from '@/lib/api-url'
import { appCopy } from '../copy'

/** Self-service password reset (owner request 2026-07-17) — wired to the
 * existing /auth/forgot-password + /auth/reset-password endpoints. The
 * forgot form always reports success (no account enumeration). */

const inputCls = 'w-full rounded-md border bg-background px-3 py-2'

function Shell({ children }: { children: React.ReactNode }) {
  const copy = useCopy(appCopy)
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6">
        <h1 className="text-xl font-semibold">{copy.shell.brand}</h1>
        {children}
      </div>
    </main>
  )
}

export function ForgotPasswordPage() {
  const copy = useCopy(appCopy)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      await apiFetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      })
    } finally {
      // Always succeed outwardly — no account enumeration.
      setSent(true)
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">
          <Copy of={copy.passwordReset.sent({ email })} components={[<span className="font-medium text-foreground" key="email" />]} />
        </p>
        <Link to="/login" className="text-sm text-primary hover:underline">
          {copy.authCommon.backToSignIn}
        </Link>
      </Shell>
    )
  }

  return (
    <Shell>
      <p className="text-sm text-muted-foreground">
        {copy.passwordReset.instructions}
      </p>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
        <input aria-label={copy.authCommon.email}
          type="email"
          required
          placeholder={copy.authCommon.email}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          autoComplete="email"
        />
        <Button type="submit" disabled={busy} className="w-full sm:w-auto">
          {busy ? copy.passwordReset.sending : copy.passwordReset.sendLink}
        </Button>
      </form>
      <Link to="/login" className="text-sm text-primary hover:underline">
        {copy.authCommon.backToSignIn}
      </Link>
    </Shell>
  )
}

export function ResetPasswordPage() {
  const copy = useCopy(appCopy)
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    if (password !== confirm) {
      setError(copy.passwordReset.mismatch)
      return
    }
    setBusy(true)
    try {
      const res = await apiFetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      const body = (await res.json()) as { error?: { code?: string; message: string } }
      if (!res.ok) {
        // Keyed on the CODE so the sentence is in the user's language; the
        // server's English is only the fallback for codes not catalogued here.
        if (body.error?.code === 'INVITE_PENDING') throw new Error(copy.passwordReset.invitePending)
        if (body.error?.code === 'NOT_FOUND') throw new Error(copy.passwordReset.invalid)
        throw new Error(body.error?.message ?? copy.passwordReset.invalid)
      }
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.passwordReset.couldNotReset)
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <Shell>
        <p className="text-sm text-destructive">{copy.passwordReset.missingToken}</p>
        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
          {copy.passwordReset.requestNew}
        </Link>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell>
        <p className="text-sm text-success">{copy.passwordReset.updated}</p>
        <Link
          to="/login"
          className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {copy.authCommon.goToSignIn}
        </Link>
      </Shell>
    )
  }

  return (
    <Shell>
      <p className="text-sm text-muted-foreground">{copy.passwordReset.choose}</p>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
        <input aria-label={copy.passwordReset.newPassword}
          type="password"
          required
          minLength={12}
          placeholder={copy.passwordReset.newPassword}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls}
          autoComplete="new-password"
        />
        <input aria-label={copy.passwordReset.confirmNewPassword}
          type="password"
          required
          minLength={12}
          placeholder={copy.passwordReset.confirmNewPassword}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={inputCls}
          autoComplete="new-password"
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full sm:w-auto">
          {busy ? copy.passwordReset.saving : copy.passwordReset.setNewPassword}
        </Button>
      </form>
    </Shell>
  )
}
